import { Check, ChevronRight } from "lucide-react"
import type { FC, ReactNode } from "react"
import { cn } from "@/lib/utils"

export type TenantSignupStepStatus = "complete" | "current" | "upcoming"

export interface TenantSignupStep {
  id: string
  label: string
  number: number
  status: TenantSignupStepStatus
}

interface Props {
  title: string
  steps: TenantSignupStep[]
  children: ReactNode
}

const StepMarker: FC<{ status: TenantSignupStepStatus }> = ({ status }) => {
  if (status === "complete") {
    return <Check className="size-4" strokeWidth={2.5} />
  }

  if (status === "current") {
    return <ChevronRight className="size-4" strokeWidth={2.5} />
  }

  return null
}

export const TenantSignupShell: FC<Props> = ({ title, steps, children }) => {
  return (
    <section className="w-full overflow-hidden bg-bg-white md:grid md:grid-cols-[176px_minmax(0,1fr)]">
      <nav
        aria-label="signup-step-navigation"
        className="hidden border-r border-border px-6 py-10 md:block"
      >
        <ol className="space-y-5">
          {steps.map((step) => (
            <li key={step.id} className="space-y-1">
              <p className="px-2 text-label-sm font-semibold text-gray">ステップ{step.number}</p>
              <div
                aria-current={step.status === "current" ? "step" : undefined}
                className={cn(
                  "flex h-7 items-center gap-1 px-2 text-label-lg font-semibold leading-[1.35] text-text",
                  step.status === "current" && "text-secondary",
                  step.status === "upcoming" && "text-gray"
                )}
              >
                <span>{step.label}</span>
                <StepMarker status={step.status} />
              </div>
            </li>
          ))}
        </ol>
      </nav>

      <div className="min-w-0">
        <div className="border-b border-border px-4 py-3 md:hidden">
          <ol className="flex gap-2 overflow-x-auto">
            {steps.map((step) => (
              <li
                key={step.id}
                aria-current={step.status === "current" ? "step" : undefined}
                className={cn(
                  "flex min-w-fit items-center gap-1 rounded-full border px-3 py-1 text-label-lg font-semibold leading-[1.35]",
                  step.status === "current" && "border-secondary bg-bg-secondary text-secondary",
                  step.status === "complete" && "border-border text-text",
                  step.status === "upcoming" && "border-border text-gray"
                )}
              >
                <span>{step.number}</span>
                <span>{step.label}</span>
                <StepMarker status={step.status} />
              </li>
            ))}
          </ol>
        </div>

        <header className="flex h-[76px] items-end justify-center px-4 pb-3 pt-5">
          <h1 className="text-center text-[18px] font-semibold leading-[1.35] text-foreground">
            {title}
          </h1>
        </header>

        <div className="px-4 pt-3">
          <div className="mx-auto w-full max-w-[393px] pb-10">{children}</div>
        </div>
      </div>
    </section>
  )
}
